import React, { useEffect, useState, Suspense, lazy } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
const SceneArrival = lazy(() => import("@/components/scenes/SceneArrival"));
const SceneGates = lazy(() => import("@/components/scenes/SceneGates"));
const SceneMemories = lazy(() => import("@/components/scenes/SceneMemories"));
const SceneStories = lazy(() => import("@/components/scenes/SceneStories"));
const SceneTimeline = lazy(() => import("@/components/scenes/SceneTimeline"));
const SceneFinale = lazy(() => import("@/components/scenes/SceneFinale"));
const WaitlistForm = lazy(() => import("@/components/WaitlistForm"));
import { ASSETS, COPY } from "@/lib/assets";
import { useTweaks } from "@/context/TweaksContext";
import { Settings } from "lucide-react";

export default function VikingLanding() {
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  const { tweaks, isAdmin } = useTweaks();
  const { appearance } = tweaks;
  const [intro, setIntro] = useState(appearance.introOverlay);

  useEffect(() => {
    if (!appearance.introOverlay) { setIntro(false); return; }
    const t = setTimeout(() => setIntro(false), 2600);
    return () => clearTimeout(t);
  }, [appearance.introOverlay]);

  return (
    <main className="relative" data-testid="viking-landing" lang="es">
      {/* Intro: black-out + animated logo + tagline */}
      {intro && (
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ delay: 1.7, duration: 0.9, ease: "easeOut" }}
          className="pointer-events-none fixed inset-0 z-[1000] flex flex-col items-center justify-center gap-6 bg-black"
          aria-hidden
        >
          {appearance.showLogoIntro && (
            <motion.img
              src={ASSETS.logo}
              alt="My Valhalla"
              initial={{ opacity: 0, scale: 0.92, filter: "blur(8px)" }}
              animate={{ opacity: [0, 1, 1, 0.95], scale: [0.92, 1, 1, 1.02], filter: ["blur(8px)", "blur(0px)", "blur(0px)", "blur(0px)"] }}
              transition={{ duration: 2.6, times: [0, 0.35, 0.75, 1] }}
              className="h-32 w-auto md:h-44"
              style={{ filter: "drop-shadow(0 0 24px rgba(252,211,77,0.35))" }}
            />
          )}
          <motion.span
            initial={{ opacity: 0, letterSpacing: "0.2em" }}
            animate={{ opacity: [0, 1, 1, 0], letterSpacing: "0.6em" }}
            transition={{ duration: 2.6, times: [0.15, 0.4, 0.8, 1] }}
            className="font-serif-display text-[10px] uppercase tracking-[0.5em] md:text-xs"
            style={{ color: appearance.accentGold }}
          >
            {COPY.intro}
          </motion.span>
        </motion.div>
      )}

      {/* Top navigation */}
      {appearance.showTopNav && (
        <nav
          data-testid="top-nav"
          className="pointer-events-none fixed inset-x-0 top-0 z-50 flex items-center justify-between px-6 py-5 md:px-10"
        >
          <a href="#scene-arrival" className="pointer-events-auto flex items-center gap-2 md:gap-3">
            {appearance.showLogoNav && (
              <img
                src={ASSETS.logo}
                alt={COPY.brand}
                className="h-8 w-auto md:h-9"
                style={{ filter: "drop-shadow(0 0 10px rgba(252,211,77,0.25))" }}
              />
            )}
            <span className="font-serif-display text-sm italic text-[#fef3c7]/85 md:text-base">
              {COPY.brand}
              <span className="mx-1.5 hidden text-[#fef3c7]/30 sm:inline md:mx-2">·</span>
              <span className="hidden sm:inline" style={{ color: appearance.accentGold }}>{COPY.brandTagline}</span>
            </span>
          </a>

          <div className="pointer-events-auto hidden items-center gap-6 font-body text-[10px] uppercase tracking-[0.4em] text-[#fef3c7]/60 md:flex">
            {COPY.navLinks.map((l) => (
              <a key={l.href} href={l.href} className="hover:text-[#fcd34d]">{l.label}</a>
            ))}
            <a
              href="#waitlist"
              data-testid="nav-cta"
              className="rounded-full border border-[#c5a059]/40 px-4 py-2 text-[9px] uppercase tracking-[0.4em] text-[#fcd34d] transition hover:border-[#fcd34d] hover:bg-[#fcd34d]/10"
            >
              {COPY.ctaPrimary}
            </a>
          </div>
        </nav>
      )}

      {/* Scroll progress */}
      {appearance.showProgressBar && (
        <motion.div
          style={{ scaleX: progress }}
          data-testid="scroll-progress"
          className="fixed left-0 right-0 top-0 z-[60] h-[2px] origin-left bg-gradient-to-r from-[#c5a059] via-[#fcd34d] to-[#d97706]"
        />
      )}

      <Suspense fallback={null}>
        <SceneArrival />
        <SceneGates />
        <SceneMemories />
        <SceneStories />
        <SceneTimeline />
        <SceneFinale />
        <WaitlistForm />
      </Suspense>

      {/* Footer */}
      <footer
        data-testid="footer"
        className="relative z-10 border-t border-[#c5a059]/15 bg-[#04070b] px-6 py-16 text-center md:px-12"
      >
        <img
          src={ASSETS.logo}
          alt={COPY.brand}
          className="mx-auto mb-6 h-16 w-auto opacity-90"
          style={{ filter: "drop-shadow(0 0 20px rgba(252,211,77,0.25))" }}
        />
        <p className="font-serif-display text-2xl font-light italic text-[#fef3c7] md:text-3xl">
          {COPY.brand}
        </p>
        <p className="font-body mt-2 text-[10px] uppercase tracking-[0.5em]" style={{ color: appearance.accentGold }}>
          {COPY.footer.main}
        </p>
        <p className="font-serif-display mt-6 max-w-xl mx-auto text-base italic text-[#fef3c7]/60 md:text-lg">
          {COPY.footer.quote}
        </p>
        {isAdmin && (
          <Link
            to="/panelAdmin"
            data-testid="footer-admin-link"
            className="mt-10 inline-block font-body text-[9px] uppercase tracking-[0.4em] text-[#fef3c7]/30 transition hover:text-[#fcd34d]"
          >
            Admin · Tweaks
          </Link>
        )}
      </footer>

      {appearance.grainOverlay && <div className="grain" aria-hidden />}
    </main>
  );
}
